import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Calendar,
  Box,
  Badge,
  Cards,
  Grid,
  ColumnLayout,
  StatusIndicator,
  Alert,
  Modal,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  DatePicker,
  TimeInput
} from '@cloudscape-design/components';

import axios from 'axios';

const API_BASE_URL = 'http://localhost:3000/api';

const FamilySchedule = ({ 
  familyMembers = [], 
  currentUser,
  onCreateTask,
  onUpdateTask 
}) => {
  const [tasks, setTasks] = useState([]);

  // 加载任务数据
  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_BASE_URL}/tasks`, {
        params: {
          family_id: currentUser?.family_id || '6869d0771dfa97eaefc3bbf7',
          date: today
        }
      });
      
      // 检查API返回的数据是否为空
      if (!response.data || response.data.length === 0) {
        setTasks([]);
        return;
      }
      
      setTasks(response.data);
    } catch (error) {
      console.error('❌ 加载任务失败:', error);
      setError('获取任务失败，请检查网络连接或稍后重试');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载任务
  useEffect(() => {
    if (currentUser) {
      loadTasks();
    }
  }, [currentUser]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 新事件表单状态
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    type: 'meeting',
    participants: []
  });

  // 重置新事件表单
  const resetNewEventForm = () => {
    setNewEvent({
      title: '',
      description: '',
      date: '',
      time: '',
      type: 'meeting',
      participants: []
    });
  };

  // 获取指定日期的任务
  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = new Date(task.due_date).toISOString().split('T')[0];
      return taskDate === date;
    });
  };

  // 获取指定日期的事件
  const getEventsForDate = (date) => {
    return events.filter(event => event.date === date);
  };

  // 获取日历标记
  const getCalendarMarkers = () => {
    const markers = [];
    
    // 为有任务的日期添加标记
    tasks.forEach(task => {
      if (task.due_date) {
        const date = new Date(task.due_date).toISOString().split('T')[0];
        if (!markers.find(m => m.date === date)) {
          markers.push({
            date: date,
            type: 'task'
          });
        }
      }
    });

    // 为有事件的日期添加标记
    events.forEach(event => {
      if (!markers.find(m => m.date === event.date)) {
        markers.push({
          date: event.date,
          type: 'event'
        });
      }
    });

    return markers;
  };

  // 处理创建事件
  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      setError('请填写事件标题和日期');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const eventData = {
        ...newEvent,
        id: Date.now().toString(),
        created_at: new Date().toISOString()
      };
      
      setEvents(prev => [...prev, eventData]);
      setShowEventModal(false);
      resetNewEventForm();
    } catch (error) {
      setError('创建事件失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 获取执行人姓名
  const getAssigneeName = (assigneeId) => {
    const member = familyMembers.find(m => m._id === assigneeId);
    return member ? member.name : '未知用户';
  };

  // 获取状态徽章
  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { color: 'blue', text: '待完成' },
      in_progress: { color: 'grey', text: '进行中' },
      completed: { color: 'green', text: '已完成' },
      overdue: { color: 'red', text: '已逾期' }
    };
    const statusInfo = statusMap[status] || statusMap.pending;
    return <Badge color={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  // 事件类型选项
  const eventTypeOptions = [
    { label: '会议', value: 'meeting' },
    { label: '活动', value: 'activity' },
    { label: '提醒', value: 'reminder' },
    { label: '生日', value: 'birthday' },
    { label: '其他', value: 'other' }
  ];

  const selectedDateTasks = getTasksForDate(selectedDate);
  const selectedDateEvents = getEventsForDate(selectedDate);

  return (
    <SpaceBetween size="l">
      {/* 错误提示 */}
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 页面标题 */}
      <Container
        header={
          <Header
            variant="h2"
            description="查看家庭日程安排和任务计划"
            actions={
              <Button
                variant="primary"
                onClick={() => setShowEventModal(true)}
              >
                添加事件
              </Button>
            }
          >
            家庭日程
          </Header>
        }
      >
        <Grid gridDefinition={[
          { colspan: { default: 12, s: 8 } },
          { colspan: { default: 12, s: 4 } }
        ]}>
          {/* 日历视图 */}
          <Container header={<Header variant="h3">日历</Header>}>
            <SpaceBetween size="m">
              <Calendar
                value={selectedDate}
                onChange={({ detail }) => setSelectedDate(detail.value)}
                locale="zh-CN"
                startOfWeek={1}
                isDateEnabled={date => true}
              />
              
              <Box>
                <Header variant="h4">
                  {new Date(selectedDate).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long'
                  })}
                </Header>
              </Box>
            </SpaceBetween>
          </Container>

          {/* 当日详情 */}
          <Container header={<Header variant="h3">当日安排</Header>}>
            <SpaceBetween size="m">
              {/* 当日任务 */}
              {selectedDateTasks.length > 0 && (
                <Box>
                  <Header variant="h4">📋 任务 ({selectedDateTasks.length})</Header>
                  <SpaceBetween size="s">
                    {selectedDateTasks.map(task => (
                      <Box key={task._id} padding="s" background="color-background-container-content">
                        <SpaceBetween size="xs">
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box fontWeight="bold">{task.title}</Box>
                            {getStatusBadge(task.status)}
                          </Box>
                          <Box fontSize="body-s" color="text-body-secondary">
                            执行人: {getAssigneeName(task.assignee_id)}
                          </Box>
                          {task.description && (
                            <Box fontSize="body-s">{task.description}</Box>
                          )}
                          <Box display="flex" alignItems="center">
                            <span style={{ color: '#ff9900', marginRight: '4px' }}>🪙</span>
                            <Box fontSize="body-s">{task.points || 0} 金币</Box>
                          </Box>
                        </SpaceBetween>
                      </Box>
                    ))}
                  </SpaceBetween>
                </Box>
              )}

              {/* 当日事件 */}
              {selectedDateEvents.length > 0 && (
                <Box>
                  <Header variant="h4">📅 事件 ({selectedDateEvents.length})</Header>
                  <SpaceBetween size="s">
                    {selectedDateEvents.map(event => (
                      <Box key={event.id} padding="s" background="color-background-container-content">
                        <SpaceBetween size="xs">
                          <Box fontWeight="bold">{event.title}</Box>
                          {event.time && (
                            <Box fontSize="body-s" color="text-body-secondary">
                              时间: {event.time}
                            </Box>
                          )}
                          {event.description && (
                            <Box fontSize="body-s">{event.description}</Box>
                          )}
                          <Badge color="blue">{eventTypeOptions.find(o => o.value === event.type)?.label}</Badge>
                        </SpaceBetween>
                      </Box>
                    ))}
                  </SpaceBetween>
                </Box>
              )}

              {/* 无安排提示 */}
              {selectedDateTasks.length === 0 && selectedDateEvents.length === 0 && (
                <Box textAlign="center" color="text-body-secondary" padding="l">
                  <Box fontSize="body-l">📅</Box>
                  <Box>这一天暂无安排</Box>
                </Box>
              )}
            </SpaceBetween>
          </Container>
        </Grid>
      </Container>

      {/* 本周概览 */}
      <Container header={<Header variant="h3">本周概览</Header>}>
        <Grid gridDefinition={[
          { colspan: { default: 12, s: 6 } },
          { colspan: { default: 12, s: 6 } }
        ]}>
          <Box>
            <Header variant="h4">📊 任务统计</Header>
            <SpaceBetween size="s">
              <Box key="total" display="flex" justifyContent="space-between">
                <span>总任务数:</span>
                <Badge color="blue">{tasks.length}</Badge>
              </Box>
              <Box key="completed" display="flex" justifyContent="space-between">
                <span>已完成:</span>
                <Badge color="green">{tasks.filter(t => t.status === 'completed').length}</Badge>
              </Box>
              <Box key="pending" display="flex" justifyContent="space-between">
                <span>待完成:</span>
                <Badge color="grey">{tasks.filter(t => t.status === 'pending').length}</Badge>
              </Box>
              <Box key="overdue" display="flex" justifyContent="space-between">
                <span>已逾期:</span>
                <Badge color="red">{tasks.filter(t => t.status === 'overdue').length}</Badge>
              </Box>
            </SpaceBetween>
          </Box>

          <Box>
            <Header variant="h4">👥 家庭成员</Header>
            <SpaceBetween size="s">
              {familyMembers.length > 0 ? (
                familyMembers.map(member => (
                  <Box key={member._id} display="flex" justifyContent="space-between">
                    <span>{member.name}</span>
                    <Box display="flex" alignItems="center">
                      <span style={{ color: '#ff9900', marginRight: '4px' }}>🪙</span>
                      <span>{member.points || 0}</span>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box color="text-body-secondary">暂无家庭成员</Box>
              )}
            </SpaceBetween>
          </Box>
        </Grid>
      </Container>

      {/* 添加事件模态框 */}
      <Modal
        onDismiss={() => {
          setShowEventModal(false);
          resetNewEventForm();
          setError(null);
        }}
        visible={showEventModal}
        closeAriaLabel="关闭模态框"
        size="medium"
        header="添加新事件"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                key="cancel"
                variant="link"
                onClick={() => {
                  setShowEventModal(false);
                  resetNewEventForm();
                  setError(null);
                }}
              >
                取消
              </Button>
              <Button
                key="submit"
                variant="primary"
                onClick={handleCreateEvent}
                loading={loading}
              >
                添加事件
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField key="title" label="事件标题" stretch>
              <Input
                value={newEvent.title}
                onChange={({ detail }) => setNewEvent(prev => ({ ...prev, title: detail.value }))}
                placeholder="输入事件标题"
              />
            </FormField>

            <FormField key="description" label="事件描述" stretch>
              <Textarea
                value={newEvent.description}
                onChange={({ detail }) => setNewEvent(prev => ({ ...prev, description: detail.value }))}
                placeholder="输入事件描述（可选）"
                rows={3}
              />
            </FormField>

            <ColumnLayout key="datetime" columns={2}>
              <FormField label="日期">
                <DatePicker
                  value={newEvent.date}
                  onChange={({ detail }) => setNewEvent(prev => ({ ...prev, date: detail.value }))}
                  placeholder="选择日期"
                />
              </FormField>

              <FormField label="时间">
                <TimeInput
                  value={newEvent.time}
                  onChange={({ detail }) => setNewEvent(prev => ({ ...prev, time: detail.value }))}
                  placeholder="选择时间（可选）"
                  format="hh:mm"
                />
              </FormField>
            </ColumnLayout>

            <FormField key="event-type" label="事件类型">
              <Select
                selectedOption={{ label: eventTypeOptions.find(o => o.value === newEvent.type)?.label, value: newEvent.type }}
                onChange={({ detail }) => setNewEvent(prev => ({ ...prev, type: detail.selectedOption.value }))}
                options={eventTypeOptions}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>
    </SpaceBetween>
  );
};

export default FamilySchedule;